import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebaseService';
import { CampaignSentEntry } from '../types';

export interface EmailTemplateContent {
  subject: string;
  body_html: string;
  cta_label?: string;
  cta_url?: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  content: Record<string, EmailTemplateContent>;
  header_image_url?: string | null;
  variables: string[];
  status: 'active' | 'draft';
  created_at: any;
  updated_at: any;
}

export interface CampaignStep {
  step_number: number;
  delay_days: number;
  template_id: string;
  enabled: boolean;
}

export interface CampaignSequence {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'draft';
  require_newsletter_consent: boolean;
  steps: CampaignStep[];
  created_at: any;
  updated_at: any;
}

export async function fetchEmailTemplates(): Promise<EmailTemplate[]> {
  const snap = await getDocs(query(collection(db, 'email_templates'), orderBy('name')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as EmailTemplate));
}

export async function fetchCampaignSequences(): Promise<CampaignSequence[]> {
  const snap = await getDocs(query(collection(db, 'campaign_sequences'), orderBy('name')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as CampaignSequence));
}

export async function sendTestCampaignEmail(opts: {
  template_id: string;
  admin_email?: string;
  header_image_url?: string;
  language?: string;
}): Promise<{ ok: boolean; sentTo: string }> {
  const callable = httpsCallable<typeof opts, { ok: boolean; sentTo: string }>(functions, 'sendTestCampaignEmail');
  const result = await callable(opts);
  return result.data;
}

export async function seedOnboardingCampaign(): Promise<{
  ok: boolean;
  templates: string[];
  campaign: string;
  featuredRoute: { id: string; name: string } | null;
}> {
  const callable = httpsCallable(functions, 'seedOnboardingCampaign');
  const result = await callable({});
  return result.data as any;
}

export interface ScheduledBroadcast {
  id: string;
  name: string;
  template_id: string;
  send_date: string;
  target_audience: string;
  target_language: string | null;
  status: 'pending' | 'paused' | 'sending' | 'sent' | 'error';
  sent_count?: number;
  error_count?: number;
  created_at: any;
}

export async function fetchScheduledBroadcasts(): Promise<ScheduledBroadcast[]> {
  const snap = await getDocs(query(collection(db, 'scheduled_broadcasts'), orderBy('send_date')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as ScheduledBroadcast));
}

export async function seedSeasonalBroadcasts(): Promise<{
  ok: boolean;
  templates: string[];
  broadcasts: { id: string; date: string }[];
}> {
  const callable = httpsCallable(functions, 'seedSeasonalBroadcasts');
  const result = await callable({});
  return result.data as any;
}

export async function seedWinterBroadcast(): Promise<{
  ok: boolean;
  template: string;
  template_created: boolean;
  broadcast: { id: string; date: string; created: boolean };
}> {
  const callable = httpsCallable(functions, 'seedWinterBroadcast');
  const result = await callable({});
  return result.data as any;
}

export async function seedTesterCampaign(): Promise<{
  ok: boolean;
  templates: string[];
  campaign: string;
}> {
  const callable = httpsCallable(functions, 'seedTesterCampaign');
  const result = await callable({});
  return result.data as any;
}

export async function updateEmailTemplate(opts: {
  template_id: string;
  language: string;
  subject?: string;
  body_html?: string;
  cta_label?: string;
  cta_url?: string;
  header_image_url?: string;
}): Promise<{ ok: boolean; template_id: string; language: string; fieldsUpdated: number }> {
  const callable = httpsCallable<typeof opts, { ok: boolean; template_id: string; language: string; fieldsUpdated: number }>(functions, 'updateEmailTemplate');
  const result = await callable(opts);
  return result.data;
}

export async function updateScheduledBroadcast(opts: {
  broadcast_id: string;
  send_date?: string;
  status?: 'pending' | 'paused';
  target_audience?: string;
}): Promise<{ ok: boolean }> {
  const callable = httpsCallable<typeof opts, { ok: boolean }>(functions, 'updateScheduledBroadcast');
  const result = await callable(opts);
  return result.data;
}

export async function fetchCampaignSentForUser(uid: string): Promise<CampaignSentEntry[]> {
  const q = query(
    collection(db, 'campaign_sent'),
    where('user_id', '==', uid),
    orderBy('sent_at', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as CampaignSentEntry));
}

export async function fetchCampaignNameMaps(): Promise<{
  templates: Map<string, string>;
  campaigns: Map<string, string>;
}> {
  const [templates, campaigns] = await Promise.all([
    fetchEmailTemplates(),
    fetchCampaignSequences(),
  ]);
  return {
    templates: new Map(templates.map(t => [t.id, t.name])),
    campaigns: new Map(campaigns.map(c => [c.id, c.name])),
  };
}
